import { Injectable } from '@angular/core';
import { Resource, Service } from 'ngx-jsonapi';
import { lastValueFrom } from 'rxjs';

export interface MusicianDirectoryEntry {
  id: string;
  userId: string;
  publicId: string;
  name: string;
  description: string;
  profilePhoto: string | null;
  instrumentId: number;
  instrumentName: string;
  skillLevel: number;
  skillLevelLabel: string;
  yearsExperience: number | null;
  styles: string[];
  distanceKm: number | null;
}

export interface GroupedMusicianCardInstrument {
  id: number;
  name: string;
  skillLevel: number;
  skillLevelLabel: string;
  yearsExperience: number | null;
}

export interface GroupedMusicianCard {
  id: string;
  userId: string;
  publicId: string;
  name: string;
  description: string;
  profilePhoto: string | null;
  instruments: GroupedMusicianCardInstrument[];
}

export interface FetchMusicianDirectoryPageOptions {
  pageNumber: number;
  pageSize?: number;
  searchQuery: string;
  instrumentId: number | null;
  skillLevel: number | null;
  styleId: number | null;
  yearsExperienceMin: number | null;
  yearsExperienceMax: number | null;
  distanceKm: number;
  userLatitude: number | null;
  userLongitude: number | null;
}

export interface MusicianDirectoryPage {
  musicians: MusicianDirectoryEntry[];
  totalResults: number;
  rangeStart: number;
  rangeEnd: number;
}

export function groupMusicianDirectoryEntries(entries: MusicianDirectoryEntry[]): GroupedMusicianCard[] {
  const cardsByUserId = new Map<string, GroupedMusicianCard>();

  for (const entry of entries) {
    const existingCard = cardsByUserId.get(entry.userId);
    const instrument: GroupedMusicianCardInstrument = {
      id: entry.instrumentId,
      name: entry.instrumentName,
      skillLevel: entry.skillLevel,
      skillLevelLabel: entry.skillLevelLabel,
      yearsExperience: entry.yearsExperience,
    };

    if (existingCard) {
      if (!existingCard.instruments.some((item) => item.id === instrument.id)) {
        existingCard.instruments.push(instrument);
      }
      continue;
    }

    cardsByUserId.set(entry.userId, {
      id: entry.id,
      userId: entry.userId,
      publicId: entry.publicId,
      name: entry.name,
      description: entry.description,
      profilePhoto: entry.profilePhoto,
      instruments: [instrument],
    });
  }

  return Array.from(cardsByUserId.values());
}

export class Musician extends Resource {
  public override attributes = {
    user_id: '',
    public_id: '',
    display_name: '',
    bio: '',
    profile_photo: null as string | null,
    instrument_id: 0,
    instrument_name: '',
    skill_level: 0,
    skill_level_label: '',
    years_experience: null as number | null,
    styles: [] as string[],
    distance_km: null as number | null,
  };

  public override relationships = {};
}

@Injectable({
  providedIn: 'root',
})
export class MusicianService extends Service<Musician> {
  private readonly directoryPageSize = 5;

  public override resource = Musician;
  public override type = 'MusicianInstrument';
  public override path = '/api/musicians';

  public async fetchDirectoryPage(options: FetchMusicianDirectoryPageOptions): Promise<MusicianDirectoryPage> {
    const page = await lastValueFrom(this.all({
      remotefilter: this.buildRemoteFilter(options),
      page: {
        number: options.pageNumber,
        size: options.pageSize ?? this.directoryPageSize,
      },
    }));

    const totalResults = Number((page as { page?: { total_resources?: number | null } }).page?.total_resources ?? page.data.length);
    const rangeStart = totalResults === 0
      ? 0
      : ((options.pageNumber - 1) * (options.pageSize ?? this.directoryPageSize)) + 1;
    const rangeEnd = totalResults === 0
      ? 0
      : rangeStart + page.data.length - 1;

    return {
      musicians: page.data.map((musician) => this.toMusicianDirectoryEntry(musician)),
      totalResults,
      rangeStart,
      rangeEnd,
    };
  }

  private buildRemoteFilter(options: FetchMusicianDirectoryPageOptions): Record<string, string> {
    const filter: Record<string, string> = {};

    const trimmedQuery = options.searchQuery.trim();
    if (trimmedQuery) {
      filter['search'] = trimmedQuery;
    }

    if (options.instrumentId !== null) {
      filter['instrument'] = String(options.instrumentId);
    }

    if (options.skillLevel !== null) {
      filter['skill_level'] = String(options.skillLevel);
    }

    if (options.styleId !== null) {
      filter['style'] = String(options.styleId);
    }

    if (options.yearsExperienceMin !== null) {
      filter['years_experience_gte'] = String(options.yearsExperienceMin);
    }

    if (options.yearsExperienceMax !== null) {
      filter['years_experience_lte'] = String(options.yearsExperienceMax);
    }

    if (
      options.distanceKm > 0
      && options.userLatitude !== null
      && options.userLongitude !== null
    ) {
      filter['user_latitude'] = String(options.userLatitude);
      filter['user_longitude'] = String(options.userLongitude);
      filter['distance_lte'] = String(options.distanceKm);
    }

    return filter;
  }

  private toMusicianDirectoryEntry(musician: Musician): MusicianDirectoryEntry {
    return {
      id: musician.id,
      userId: musician.attributes.user_id,
      publicId: musician.attributes.public_id,
      name: musician.attributes.display_name,
      description: musician.attributes.bio,
      profilePhoto: musician.attributes.profile_photo,
      instrumentId: Number(musician.attributes.instrument_id),
      instrumentName: musician.attributes.instrument_name,
      skillLevel: Number(musician.attributes.skill_level),
      skillLevelLabel: musician.attributes.skill_level_label,
      yearsExperience: musician.attributes.years_experience !== null
        ? Number(musician.attributes.years_experience)
        : null,
      styles: musician.attributes.styles ?? [],
      distanceKm: musician.attributes.distance_km !== null
        ? Number(musician.attributes.distance_km)
        : null,
    };
  }
}
